import { useCallback, useContext } from "react";
import { UI } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./InvoiceAccounting.module.css";
import { IAccounting } from "src/stores/krediflow/InvoiceTypes";
import { ErpType } from "src/network/User";
import { RealestateField } from "./RealestateField";
import { AccountField } from "./AccountField";
import { CostcenterField } from "./CostcenterField";
import { BookingTextField } from "./BookingTextField";
import { AmountField } from "./AmountField";
import { QuantityField } from "./QuantityField";
import { ExtraCostDateField } from "./ExtraCostDateField";
import { VatCodeField } from "./VatCodeField";
import { VatRateField } from "./VatRateField";
import { NetAmountField } from "./NetAmountField";
import { useTranslation } from "react-i18next";

interface InvoiceAccountingProperties {
    accounting: IAccounting;
    accountingIndex: number;
}

const InvoiceAccountingBase = (props: InvoiceAccountingProperties) => {
    const { invoiceStore, authStore, accountingAccountSearchStore } = useContext(RootStoreContext);

    const { t } = useTranslation();

    const { accountingIndex } = props;

    const removeAccountingRow = useCallback(
        (index: number) => () => {
            invoiceStore.currentInvoice?.removeAccountingRow(index);
        },
        [invoiceStore]
    );

    const accountField = <AccountField accountingRowIndex={accountingIndex} />;
    const costCenterField = <CostcenterField accountingRowIndex={accountingIndex} />;
    const bookingTextField = <BookingTextField accountingRowIndex={accountingIndex} />;

    const amountField = <AmountField accountingRowIndex={accountingIndex} />;

    const removeAccountingRowIcon =
        accountingIndex > 0 ? (
            <UI.Icon
                icon={UI.SVGIcon.Trash}
                size={30}
                className={`ml-2 ${styles.DeleteIcon}`}
                onClick={removeAccountingRow(accountingIndex)}
            />
        ) : (
            <></>
        );

    const quantityField = <QuantityField accountingRowIndex={accountingIndex} />;
    const extraCostDateField = <ExtraCostDateField accountingRowIndex={accountingIndex} />;
    const vatCodeField = <VatCodeField accountingRowIndex={accountingIndex} />;
    const vatRateField = <VatRateField accountingRowIndex={accountingIndex} />;
    const netAmountField = <NetAmountField accountingRowIndex={accountingIndex} />;

    const erpType = authStore.user?.erpType;

    const accountingColumnsIT2 = (
        <>
            {/* First Row (12 Columns) */}
            <UI.Col
                className={
                    accountingAccountSearchStore.areAccountTagsDisplayedByIndex[accountingIndex]
                        ? styles.ExtraPadding
                        : ""
                }
                lg={invoiceStore.currentInvoice?.canEditAccountingCostcenterField(accountingIndex) ? 3 : 6}
            >
                {accountField}
            </UI.Col>
            <UI.Col
                className={
                    invoiceStore.currentInvoice?.canEditAccountingCostcenterField(accountingIndex) ? "" : "d-none"
                }
                lg={3}
            >
                {costCenterField}
            </UI.Col>
            <UI.Col lg={4}>{bookingTextField}</UI.Col>
            <UI.Col lg={2}>
                <div className="d-flex">
                    <div style={{ width: "84%" }}>{amountField}</div>
                    <div style={{ width: "16%" }}>{removeAccountingRowIcon}</div>
                </div>
            </UI.Col>

            {/* Second Row (12 Columns) */}
            <UI.Col
                lg={2}
                className={` ${
                    invoiceStore.currentInvoice?.canEditAccountingQuantityField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
            >
                {quantityField}
            </UI.Col>
            <UI.Col
                className={
                    invoiceStore.currentInvoice?.canSeeAccountingVatCodeField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }
                lg={2}
            >
                {vatCodeField}
            </UI.Col>
            <UI.Col
                className={
                    invoiceStore.currentInvoice?.canSeeAccountingVatCodeField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }
                lg={2}
            >
                {vatRateField}
            </UI.Col>
            <UI.Col lg={2}>{extraCostDateField}</UI.Col>
            <UI.Col lg={2}>
                <span
                    className={
                        invoiceStore.currentInvoice?.canSeeAccountingNetAmountField(accountingIndex)
                            ? styles.VatAmountText
                            : "invisible"
                    }
                >
                    {t("screens.kredi_flow.accountings.excl_vat")}
                </span>
            </UI.Col>
            <UI.Col
                lg={2}
                className={
                    invoiceStore.currentInvoice?.canSeeAccountingNetAmountField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }
            >
                <div className="d-flex">
                    <div style={{ width: "84%" }}>{netAmountField}</div>
                    <div style={{ width: "16%" }}></div>
                </div>
            </UI.Col>
        </>
    );

    const accountingColumnsRIMO = (
        <>
            {/* First Row (12 Columns) */}
            <UI.Col lg={3}>{<RealestateField accountingRowIndex={accountingIndex} />}</UI.Col>
            <UI.Col
                className={
                    accountingAccountSearchStore.areAccountTagsDisplayedByIndex[accountingIndex]
                        ? styles.ExtraPadding
                        : ""
                }
                lg={3}
            >
                {accountField}
            </UI.Col>
            <UI.Col lg={4}>{bookingTextField}</UI.Col>
            <UI.Col lg={2}>
                <div className="d-flex">
                    <div style={{ width: "84%" }}>{amountField}</div>
                    <div style={{ width: "16%" }}>{removeAccountingRowIcon}</div>
                </div>
            </UI.Col>

            {/* Second Row (12 Columns) */}
            <UI.Col
                lg={2}
                className={` ${
                    invoiceStore.currentInvoice?.canEditAccountingQuantityField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }`}
            >
                {quantityField}
            </UI.Col>
            <UI.Col
                className={
                    invoiceStore.currentInvoice?.canSeeAccountingVatCodeField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }
                lg={2}
            >
                {vatCodeField}
            </UI.Col>
            <UI.Col
                className={
                    invoiceStore.currentInvoice?.canSeeAccountingVatCodeField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }
                lg={2}
            >
                {vatRateField}
            </UI.Col>
            <UI.Col lg={2}>{extraCostDateField}</UI.Col>
            <UI.Col lg={2}>
                <span
                    className={
                        invoiceStore.currentInvoice?.canSeeAccountingNetAmountField(accountingIndex)
                            ? styles.VatAmountText
                            : "invisible"
                    }
                >
                    {t("screens.kredi_flow.accountings.excl_vat")}
                </span>
            </UI.Col>
            <UI.Col
                lg={2}
                className={
                    invoiceStore.currentInvoice?.canSeeAccountingNetAmountField(accountingIndex)
                        ? "d-flex align-items-end"
                        : "invisible"
                }
            >
                <div className="d-flex">
                    <div style={{ width: "84%" }}>{netAmountField}</div>
                    <div style={{ width: "16%" }}></div>
                </div>
            </UI.Col>
        </>
    );

    const accountingValuationErrorString =
        invoiceStore.currentInvoice?.getAccountingValuationErrorString(accountingIndex);

    return (
        <>
            <UI.Row className={`${styles.AccountingRowContainer} ${accountingIndex > 0 ? "mt-2" : ""}`}>
                {erpType === ErpType.IT2
                    ? accountingColumnsIT2
                    : erpType === ErpType.RIMO
                    ? accountingColumnsRIMO
                    : undefined}
            </UI.Row>
            {accountingValuationErrorString ? (
                <div style={{ textAlign: "right" }}>
                    <span className="error-text">{accountingValuationErrorString}</span>
                </div>
            ) : (
                ""
            )}
        </>
    );
};

export const InvoiceAccounting = Sentry.withProfiler(observer(InvoiceAccountingBase));
